import { useEffect, useRef } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { CustomEditable } from 'src/components/RichSlate';

import { ContinueRowFloatingPanel } from '../ContinueRowFloatingPanel';

import { useHandlers } from './useHandlers';
import { useStyles } from './useStyles';

const PLACEHOLDER = 'Tell us what to write ';

export const InstructionsTextarea = ({
  className,
  classes,
  onMenuButtonClick,
  setWasOpened,
  editor,
  onMenuOpen,
  onCloseMenu,
  onKeyDown,
  onEditorFocus,
  setMenuType,
  shortExpanding,
  noTags,
  placeholder,
  renderPlaceholder,
  footerShown,
  plusButtonRef,
  setPlusButtonRef,
  isFirstField,
  noMinMaxHeight,
  onQuickSearchTriggered,
  disabled,
  hideContinueRowFloatingPanel,
  disableKeyUpAction,
  isPromptField,
}) => {
  const styles = useStyles({
    classes,
    shortExpanding,
    footerShown,
    noMinMaxHeight,
  });

  const rootRef = useRef();

  const { handleKeyUp, handlePaste, handleAddClick, handleTextAreaClick, handleFocusChange } =
    useHandlers({
      editor,
      onMenuOpen,
      onCloseMenu,
      setMenuType,
      noTags,
      onQuickSearchTriggered,
      onMenuButtonClick,
      setWasOpened,
      onEditorFocus,
      setPlusButtonRef,
      rootRef,
    });

  useEffect(() => {
    if (isFirstField) {
      setPlusButtonRef?.(rootRef?.current);
    }
  }, [isFirstField, setPlusButtonRef]);

  return (
    <div
      className={cx(styles.root, className, {
        [styles.hideAddInfo]: hideContinueRowFloatingPanel,
        [styles.disabled]: disabled,
      })}
      onClick={handleTextAreaClick}
      ref={rootRef}
    >
      {!hideContinueRowFloatingPanel && (!plusButtonRef || rootRef?.current === plusButtonRef) && (
        <ContinueRowFloatingPanel
          onAddClick={handleAddClick}
          isPromptField={isPromptField}
          disabled={disabled}
        />
      )}

      <CustomEditable
        className={styles.editable}
        onKeyUp={disableKeyUpAction ? null : handleKeyUp}
        placeholder={placeholder || PLACEHOLDER}
        renderPlaceholder={renderPlaceholder}
        onKeyDown={onKeyDown}
        onPaste={handlePaste}
        data-testid="promptTextBox"
        readOnly={disabled}
        onFocusChange={handleFocusChange}
      />
    </div>
  );
};

InstructionsTextarea.propTypes = {
  setWasOpened: PropTypes.func.isRequired,
  onMenuButtonClick: PropTypes.func.isRequired,
  className: PropTypes.string,
  editor: PropTypes.shape().isRequired,
  onMenuOpen: PropTypes.func.isRequired,
  onCloseMenu: PropTypes.func.isRequired,
  classes: PropTypes.shape(),
  onKeyDown: PropTypes.func.isRequired,
  onEditorFocus: PropTypes.func,
  setMenuType: PropTypes.func.isRequired,
  shortExpanding: PropTypes.bool,
  noTags: PropTypes.bool,
  placeholder: PropTypes.string,
  renderPlaceholder: PropTypes.func,
  footerShown: PropTypes.bool,
  setPlusButtonRef: PropTypes.func,
  plusButtonRef: PropTypes.shape(),
  isFirstField: PropTypes.bool,
  noMinMaxHeight: PropTypes.bool,
  onQuickSearchTriggered: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  hideContinueRowFloatingPanel: PropTypes.bool,
  disableKeyUpAction: PropTypes.bool,
  isPromptField: PropTypes.bool,
};

InstructionsTextarea.defaultProps = {
  className: null,
  classes: null,
  onEditorFocus: null,
  shortExpanding: false,
  noTags: false,
  placeholder: '',
  renderPlaceholder: undefined,
  footerShown: false,
  setPlusButtonRef: null,
  plusButtonRef: null,
  isFirstField: false,
  noMinMaxHeight: false,
  disabled: false,
  hideContinueRowFloatingPanel: false,
  disableKeyUpAction: false,
  isPromptField: true,
};
